import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCopilot } from "@/context/CopilotContext";
import GrowthOverview from "@/components/GrowthOverview";
import { ShieldAlert, Users, Building2, Trash2, LogIn, Ban, Undo2, BadgeCheck, Sparkles } from "lucide-react";

export default function SuperAdmin() {
  const { user, refreshMe } = useAuth();
  const { startDemo } = useCopilot();
  const navigate = useNavigate();
  const [tab, setTab] = useState("workspaces");
  const [workspaces, setWorkspaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [w, u] = await Promise.all([
        api.get("/superadmin/workspaces"),
        api.get("/superadmin/users"),
      ]);
      setWorkspaces(w.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patchUser = async (id, body, successMsg) => {
    try {
      await api.patch(`/superadmin/users/${id}`, body);
      toast.success(successMsg || "Updated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteWorkspace = async (workspace_id, email) => {
    if (!window.confirm(`Delete the entire workspace of ${email}? This wipes ALL their contacts, deals, workflows, and invited members. This cannot be undone.`)) return;
    try {
      const { data } = await api.delete(`/superadmin/workspaces/${workspace_id}`);
      toast.success(`Workspace deleted. Removed ${Object.values(data.deleted).reduce((a,b)=>a+b,0)} records.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const impersonate = async (id, email) => {
    if (!window.confirm(`Log in as ${email}? You will see their data as they see it. A red banner will let you return to your admin account.`)) return;
    try {
      await api.post(`/superadmin/users/${id}/impersonate`);
      toast.success(`Impersonating ${email}`);
      await refreshMe();
      navigate("/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!user?.is_superadmin) {
    return (
      <div className="p-16 text-center">
        <ShieldAlert className="w-12 h-12 mx-auto text-[var(--peak-accent)]" />
        <h1 className="text-3xl font-heading font-black mt-4">SUPERADMIN ONLY</h1>
        <p className="text-[var(--peak-muted)] mt-2">This page is restricted to the platform operator.</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10" data-testid="superadmin-page">
      <header className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="section-label text-[var(--peak-accent)]">PLATFORM CONTROL</div>
          <h1 className="text-4xl md:text-5xl font-heading font-black tracking-tighter mt-1">SUPER ADMIN</h1>
          <p className="text-[var(--peak-muted)] mt-2">Every workspace, every seat, every record. Full read/write authority.</p>
        </div>
        <button
          onClick={async () => {
            try {
              await startDemo();
              toast.success("Demo onboarding copilot launched");
            } catch (e) {
              toast.error(formatApiError(e));
            }
          }}
          data-testid="launch-copilot-demo-btn"
          className="mt-2 flex items-center gap-2 px-5 py-3 bg-[var(--peak-accent)] text-white border-2 border-[var(--peak-ink)] shadow-[4px_4px_0_0_rgba(0,0,0,0.9)] hover:shadow-[6px_6px_0_0_rgba(0,0,0,0.9)] hover:-translate-y-0.5 transition-all font-heading font-black uppercase text-xs tracking-wider"
        >
          <Sparkles className="w-4 h-4" strokeWidth={2.5} />
          Launch Copilot Demo
        </button>
      </header>

      <GrowthOverview />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--peak-ink)] mb-6">
        <button
          data-testid="tab-workspaces"
          onClick={() => setTab("workspaces")}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "workspaces" ? "bg-[var(--peak-ink)] text-white" : "text-[var(--peak-ink)] hover:bg-[var(--peak-ink)]/5"}`}
        >
          <Building2 className="w-4 h-4" /> Workspaces ({workspaces.length})
        </button>
        <button
          data-testid="tab-users"
          onClick={() => setTab("users")}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "users" ? "bg-[var(--peak-ink)] text-white" : "text-[var(--peak-ink)] hover:bg-[var(--peak-ink)]/5"}`}
        >
          <Users className="w-4 h-4" /> All Users ({users.length})
        </button>
      </div>

      {loading && <div className="text-sm text-[var(--peak-muted)]">Loading…</div>}

      {tab === "workspaces" && !loading && (
        <WorkspaceTable
          workspaces={workspaces}
          onDelete={deleteWorkspace}
          onImpersonate={impersonate}
          onToggleFreePlan={(id, cur) => patchUser(id, { plan_override_free: !cur }, cur ? "Free plan revoked" : "Free plan granted")}
          onSuspend={(id, cur) => patchUser(id, { suspended: !cur }, cur ? "Unsuspended" : "Suspended")}
        />
      )}

      {tab === "users" && !loading && (
        <UsersTable
          users={users}
          onImpersonate={impersonate}
          onSuspend={(id, cur) => patchUser(id, { suspended: !cur }, cur ? "Unsuspended" : "Suspended")}
        />
      )}
    </div>
  );
}

function WorkspaceTable({ workspaces, onDelete, onImpersonate, onToggleFreePlan, onSuspend }) {
  return (
    <div className="border border-[var(--peak-ink)] bg-white overflow-x-auto" data-testid="workspaces-table">
      <table className="w-full text-sm">
        <thead className="bg-[var(--peak-bg)] border-b border-[var(--peak-ink)]">
          <tr className="text-left">
            <Th>Owner</Th>
            <Th>Plan</Th>
            <Th>Seats</Th>
            <Th>Contacts</Th>
            <Th>Deals</Th>
            <Th>Workflows</Th>
            <Th>Inbox</Th>
            <Th>Status</Th>
            <Th className="text-right pr-4">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {workspaces.map(w => (
            <tr key={w.workspace_id} className="border-b border-[var(--peak-border)] hover:bg-[var(--peak-bg)]" data-testid={`workspace-row-${w.workspace_id}`}>
              <Td>
                <div className="font-semibold">{w.owner_name}</div>
                <div className="text-xs text-[var(--peak-muted)] font-mono">{w.owner_email}</div>
              </Td>
              <Td>
                <Badge tone={w.plan === "pro" ? "black" : w.plan === "starter" ? "blue" : w.plan === "enterprise" ? "red" : "gray"}>
                  {w.plan}
                </Badge>
              </Td>
              <Td>{w.stats.seats}</Td>
              <Td>{w.stats.contacts}</Td>
              <Td>{w.stats.deals}</Td>
              <Td>{w.stats.workflows}</Td>
              <Td>{w.stats.inbox_messages}</Td>
              <Td>
                <div className="flex flex-col gap-1">
                  {w.suspended && <Badge tone="red">SUSPENDED</Badge>}
                  {w.plan_override_free && <Badge tone="blue">FREE (override)</Badge>}
                  {!w.suspended && !w.plan_override_free && <Badge tone="gray">Active</Badge>}
                </div>
              </Td>
              <Td className="text-right pr-4">
                <div className="flex justify-end gap-1">
                  <IconBtn title="Impersonate" onClick={() => onImpersonate(w.workspace_id, w.owner_email)} testId={`impersonate-${w.workspace_id}`}>
                    <LogIn className="w-3.5 h-3.5" />
                  </IconBtn>
                  <IconBtn title={w.plan_override_free ? "Revoke free plan" : "Grant free plan"} onClick={() => onToggleFreePlan(w.workspace_id, w.plan_override_free)}>
                    <BadgeCheck className="w-3.5 h-3.5" />
                  </IconBtn>
                  <IconBtn title={w.suspended ? "Unsuspend" : "Suspend"} onClick={() => onSuspend(w.workspace_id, w.suspended)}>
                    {w.suspended ? <Undo2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                  </IconBtn>
                  <IconBtn title="Delete workspace" tone="red" onClick={() => onDelete(w.workspace_id, w.owner_email)} testId={`delete-workspace-${w.workspace_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </IconBtn>
                </div>
              </Td>
            </tr>
          ))}
          {workspaces.length === 0 && (
            <tr><td colSpan={9} className="p-8 text-center text-[var(--peak-muted)]">No workspaces yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function UsersTable({ users, onImpersonate, onSuspend }) {
  return (
    <div className="border border-[var(--peak-ink)] bg-white overflow-x-auto" data-testid="users-table">
      <table className="w-full text-sm">
        <thead className="bg-[var(--peak-bg)] border-b border-[var(--peak-ink)]">
          <tr className="text-left">
            <Th>User</Th>
            <Th>Role</Th>
            <Th>Workspace Owner</Th>
            <Th>Status</Th>
            <Th className="text-right pr-4">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} className="border-b border-[var(--peak-border)] hover:bg-[var(--peak-bg)]" data-testid={`user-row-${u.id}`}>
              <Td>
                <div className="font-semibold">{u.name || u.email}</div>
                <div className="text-xs text-[var(--peak-muted)] font-mono">{u.email}</div>
              </Td>
              <Td>
                <Badge tone={u.role === "admin" ? "black" : u.is_owner ? "blue" : "gray"}>
                  {u.role || "user"}{u.is_owner ? " · owner" : ""}
                </Badge>
              </Td>
              <Td className="text-xs font-mono text-[var(--peak-muted)]">{u.workspace_owner_email}</Td>
              <Td>
                <div className="flex flex-col gap-1">
                  {u.suspended && <Badge tone="red">SUSPENDED</Badge>}
                  {u.plan_override_free && <Badge tone="blue">FREE (override)</Badge>}
                  {!u.suspended && !u.plan_override_free && <Badge tone="gray">Active</Badge>}
                </div>
              </Td>
              <Td className="text-right pr-4">
                <div className="flex justify-end gap-1">
                  <IconBtn title="Impersonate" onClick={() => onImpersonate(u.id, u.email)} testId={`impersonate-user-${u.id}`}>
                    <LogIn className="w-3.5 h-3.5" />
                  </IconBtn>
                  <IconBtn title={u.suspended ? "Unsuspend" : "Suspend"} onClick={() => onSuspend(u.id, u.suspended)}>
                    {u.suspended ? <Undo2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                  </IconBtn>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- small primitives ---
const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[var(--peak-muted)] ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);
const Badge = ({ tone = "gray", children }) => {
  const tones = {
    gray: "bg-[var(--peak-border)] text-[var(--peak-ink)]",
    red: "bg-[var(--peak-accent)] text-white",
    blue: "bg-[var(--peak-blue-deep)] text-white",
    black: "bg-[var(--peak-ink)] text-white",
  };
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tones[tone]}`}>{children}</span>;
};
const IconBtn = ({ children, onClick, title, tone, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    title={title}
    className={`p-1.5 border border-[var(--peak-ink)] transition-colors ${tone === "red" ? "hover:bg-[var(--peak-accent)] hover:text-white hover:border-[var(--peak-accent)]" : "hover:bg-[var(--peak-ink)] hover:text-white"}`}
  >
    {children}
  </button>
);
